import { FC, useState } from "react";

import ContentAvatar, {
  LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE,
  MediaThumbnail,
  VideoIndicatorOverlay,
} from "@src/features/contentLibrary/ContentAvatar";
import {
  getThumbnailUrl,
  isVideoMedia,
} from "@src/features/contentDetail/utils/media";
import { useOpenGraphDataQuery } from "@src/features/contentDetail/ui/MediaPreview.generated";
import { OgData } from "@src/features/libraryItem/course/OpenGraphLinkPreview";
import VideoOutlineIcon from "@src/lib/icons/18px/video-outline";
import {
  getLinkType,
  getMuxVideoId,
  isMux,
  LinkType,
} from "@src/lib/utils/files";
import { cn } from "@src/lib/utils/cn";
import PdfChipPreview from "@src/features/messaging/new/chats/PdfChipPreview";
import { NewMessagingPostFragment } from "./SentPosts.generated";
import { claimClick } from "./PostCard";

export type PostMediaTileMedia = NonNullable<
  NewMessagingPostFragment["messageBlastMedias"]
>[number];

type Props = {
  attachment: Pick<PostMediaTileMedia, "media" | "libraryItem">;
  onClick?: () => void;
  videoIconClassName?: string;
  chip?: boolean;
};

const OG_LINK_TYPES = [LinkType.LINK, LinkType.WEBSITE, LinkType.HOSTED_VIDEO];

export const getTileUrl = (
  media: NonNullable<PostMediaTileMedia["media"]>,
): string => {
  if (isVideoMedia(media) && isMux(media.mediaUrls.en)) {
    return media.mediaUrls.en;
  }
  const url = getThumbnailUrl(media);
  if (media.thumbnailImageUrl && getLinkType(url) === LinkType.PDF) {
    return media.thumbnailImageUrl;
  }
  return url;
};

// Mux builds a video's animated GIF on first request, which can take 10s+ or
// fail. The static thumbnail is quick, so show it first (letterboxed, like
// the video) and fade the GIF in over it once it has loaded.
const MuxVideoTile: FC<{
  url: string;
  videoIconClassName: string;
  chip?: boolean;
}> = ({ url, videoIconClassName, chip }) => {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [gifLoaded, setGifLoaded] = useState(false);
  const muxId = getMuxVideoId(url);
  const width = chip ? 160 : 960;
  const imageClassName = "absolute inset-0 size-full object-contain";

  return (
    <div className="relative size-full overflow-hidden bg-black">
      {thumbnailFailed && !gifLoaded ? (
        <div className="flex size-full items-center justify-center">
          <VideoOutlineIcon className="text-icon-tertiary" />
        </div>
      ) : (
        <img
          className={imageClassName}
          src={`https://image.mux.com/${muxId}/thumbnail.jpg?width=${width}`}
          alt=""
          onError={() => setThumbnailFailed(true)}
        />
      )}
      {!chip && (
        <img
          className={cn(
            imageClassName,
            "transition-opacity",
            gifLoaded ? "opacity-100" : "opacity-0",
          )}
          src={`https://image.mux.com/${muxId}/animated.gif`}
          alt=""
          onLoad={() => setGifLoaded(true)}
        />
      )}
      <VideoIndicatorOverlay iconClassName={videoIconClassName} />
    </div>
  );
};

const PostMediaTile: FC<Props> = ({
  attachment,
  onClick,
  videoIconClassName = "size-12",
  chip,
}) => {
  const { media, libraryItem } = attachment;

  const url = media && !libraryItem ? getTileUrl(media) : "";
  const isPendingPdf =
    !!media &&
    !libraryItem &&
    !media.thumbnailImageUrl &&
    getLinkType(url) === LinkType.PDF;
  const ogQuery = useOpenGraphDataQuery({
    variables: { url },
    skip: !url || isPendingPdf || !OG_LINK_TYPES.includes(getLinkType(url)),
  });

  const content = libraryItem ? (
    <ContentAvatar
      className="size-full rounded-none object-contain"
      style={{ borderRadius: 0 }}
      defaultBackgroundHashKey={libraryItem.id}
      contentType={LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE[libraryItem.type]}
      coverImage={libraryItem.coverImage}
    />
  ) : media && isPendingPdf && chip ? (
    <PdfChipPreview mediaUrl={url} />
  ) : media && isPendingPdf ? (
    <div className="size-full overflow-hidden bg-gray-2">
      {/* Inert so the tile is a static preview: wheel and drag scroll the
          page instead of the PDF, and clicks reach the tile wrapper. */}
      <iframe
        className="pointer-events-none size-full border-0 outline-none"
        title="PDF"
        src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
        tabIndex={-1}
        aria-hidden
      />
    </div>
  ) : media && isVideoMedia(media) && isMux(url) ? (
    <MuxVideoTile
      url={url}
      videoIconClassName={videoIconClassName}
      chip={chip}
    />
  ) : media ? (
    <MediaThumbnail
      // Videos letterbox so the whole frame shows; MediaThumbnail sets
      // objectFit inline, so the override has to be inline too.
      className={cn(
        "size-full rounded-none",
        isVideoMedia(media) ? "bg-black object-contain" : "object-cover",
      )}
      style={{
        borderRadius: 0,
        objectFit: isVideoMedia(media) ? "contain" : "cover",
      }}
      url={url}
      ogData={ogQuery.data?.OpenGraphData as OgData | undefined}
      videoIconClassName={videoIconClassName}
    />
  ) : null;

  if (!content) return null;

  return (
    <div
      className={cn("size-full", onClick && "cursor-pointer")}
      onClick={onClick && claimClick(onClick)}
    >
      {content}
    </div>
  );
};

export default PostMediaTile;
